import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CONTRACTOR_SERVICES = [
  { value: 'builders', label: 'Builders', icon: 'fa-hard-hat' },
  { value: 'tilers', label: 'Tilers', icon: 'fa-th-large' },
  { value: 'roofers', label: 'Roofers', icon: 'fa-home' },
  { value: 'plumbers', label: 'Plumbers', icon: 'fa-faucet' },
  { value: 'electricians', label: 'Electricians', icon: 'fa-bolt' },
  { value: 'architects', label: 'Architects', icon: 'fa-drafting-compass' },
  { value: 'painters', label: 'Painters', icon: 'fa-paint-roller' },
  { value: 'landscapers', label: 'Landscapers', icon: 'fa-tree' },
  { value: 'carpenters', label: 'Carpenters', icon: 'fa-hammer' }
];

const HomeServices = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLocation, setSearchLocation] = useState(searchParams.get('location') || '');
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || '');
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async (filters = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.service || selectedService) {
        params.append('service', filters.service || selectedService);
      }
      if (filters.location || searchLocation) {
        params.append('location', filters.location || searchLocation);
      }
      if (showVerifiedOnly) {
        params.append('verified', 'true');
      }

      const response = await axios.get(`${API}/contractors?${params.toString()}`);
      // Filter to only show contractors with active subscriptions
      const activeContractors = response.data.filter(c => 
        c.subscriptionStatus === 'active' || c.subscriptionStatus === 'trial'
      );
      setContractors(activeContractors);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      toast.error('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchContractors({ service: selectedService, location: searchLocation });
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    fetchContractors({ service, location: searchLocation });
  };

  const clearFilters = () => {
    setSelectedService('');
    setSearchLocation('');
    setShowVerifiedOnly(false);
    fetchContractors({});
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i 
          key={i} 
          className={`fas fa-star ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
        ></i>
      );
    }
    return stars;
  };

  const getServiceLabel = (value) => {
    const service = CONTRACTOR_SERVICES.find(s => s.value === value);
    return service ? service.label : value;
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <i className="fas fa-tools text-emerald-600 mr-3"></i>
            Home Services
          </h1>
          <p className="text-gray-600 text-lg">Find trusted contractors for your home projects</p>
        </div>

        {/* Service Categories */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Browse by Service</h2>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-3">
            {CONTRACTOR_SERVICES.map((service) => (
              <button
                key={service.value}
                onClick={() => handleServiceClick(service.value)}
                data-testid={`service-${service.value}`}
                className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                  selectedService === service.value
                    ? 'border-emerald-500 bg-emerald-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow'
                }`}
              >
                <i className={`fas ${service.icon} text-2xl ${
                  selectedService === service.value ? 'text-emerald-600' : 'text-gray-500'
                }`}></i>
                <span className={`text-xs mt-2 font-medium ${
                  selectedService === service.value ? 'text-emerald-700' : 'text-gray-600'
                }`}>
                  {service.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Search & Filter Bar */}
        <Card className="mb-8">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by location (e.g., Johannesburg, Sandton)"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  data-testid="location-search"
                  className="w-full"
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={selectedService || "all"} onValueChange={(val) => setSelectedService(val === "all" ? "" : val)}>
                  <SelectTrigger data-testid="service-filter">
                    <SelectValue placeholder="All Services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {CONTRACTOR_SERVICES.map((service) => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSearch} className="bg-emerald-600 hover:bg-emerald-700" data-testid="search-btn">
                  <i className="fas fa-search mr-2"></i> Search
                </Button>
                <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                  <i className="fas fa-times mr-2"></i> Clear
                </Button>
              </div>
            </div>
            
            {/* Verified Only Toggle */}
            <div className="mt-4 flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showVerifiedOnly}
                  onChange={(e) => {
                    setShowVerifiedOnly(e.target.checked);
                    fetchContractors({ service: selectedService, location: searchLocation });
                  }}
                  className="mr-2 h-4 w-4 accent-emerald-600"
                  data-testid="verified-only-toggle"
                />
                <span className="text-sm text-gray-600">
                  <i className="fas fa-check-circle text-blue-500 mr-1"></i>
                  Show verified contractors only
                </span>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {selectedService ? `${getServiceLabel(selectedService)}` : 'All Contractors'}
            <span className="text-gray-500 font-normal text-base ml-2">({contractors.length} found)</span>
          </h2>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <i className="fas fa-spinner fa-spin text-4xl text-emerald-600"></i>
            <p className="mt-4 text-gray-600">Loading contractors...</p>
          </div>
        ) : contractors.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <i className="fas fa-hard-hat text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Contractors Found</h3>
              <p className="text-gray-500 mb-4">
                {selectedService || searchLocation 
                  ? 'Try adjusting your search filters' 
                  : 'Be the first to register as a contractor!'}
              </p>
              <Button onClick={clearFilters} variant="outline">
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contractors.map((contractor) => (
              <Card 
                key={contractor.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/contractor/${contractor.id}`)}
                data-testid={`contractor-card-${contractor.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-3">
                    {/* Contractor profile image — falls back to a branded FH initials avatar */}
                    {contractor.profileImage ? (
                      <img
                        src={contractor.profileImage}
                        alt={contractor.businessName}
                        className="w-14 h-14 rounded-full object-cover border-2 border-emerald-100 flex-shrink-0"
                        data-testid={`contractor-avatar-${contractor.id}`}
                      />
                    ) : (
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-emerald-500 to-emerald-700 text-white font-bold text-lg select-none border-2 border-emerald-100"
                        aria-label="Flippy Homes default contractor logo"
                        data-testid={`contractor-avatar-placeholder-${contractor.id}`}
                      >
                        FH
                      </div>
                    )}

                    <div className="flex justify-between items-start gap-2 flex-1 min-w-0">
                      <div className="min-w-0">
                        <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                          <span className="truncate">{contractor.businessName}</span>
                          {contractor.isVerified && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                              <i className="fas fa-check-circle mr-1"></i> Verified
                            </Badge>
                          )}
                        </CardTitle>
                        <p className="text-sm text-gray-500 truncate">{contractor.contractorName}</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex">{renderStars(Math.round(contractor.averageRating))}</div>
                    <span className="text-sm text-gray-600">
                      ({contractor.averageRating?.toFixed(1) || '0.0'}) · {contractor.totalReviews || 0} reviews
                    </span>
                  </div>

                  {/* Services */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {contractor.services?.slice(0, 3).map((service) => (
                      <Badge key={service} variant="outline" className="text-xs">
                        {getServiceLabel(service)}
                      </Badge>
                    ))}
                    {contractor.services?.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{contractor.services.length - 3} more
                      </Badge>
                    )}
                  </div>

                  {/* Location */}
                  <p className="text-sm text-gray-600 mb-3">
                    <i className="fas fa-map-marker-alt text-emerald-600 mr-2"></i>
                    {contractor.location}
                    {contractor.areasServed?.length > 0 && (
                      <span className="text-gray-400"> · Serves {contractor.areasServed.length} areas</span>
                    )}
                  </p>

                  {/* Description */}
                  {contractor.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                      {contractor.description}
                    </p>
                  )}

                  {/* Contact Button */}
                  <a
                    href={`https://wa.me/${contractor.whatsappNumber?.replace(/\D/g, '')}?text=Hi, I found your profile on Flippy Homes and would like to discuss a project.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="w-full"
                    data-testid={`whatsapp-btn-${contractor.id}`}
                  >
                    <Button className="w-full bg-green-600 hover:bg-green-700">
                      <i className="fab fa-whatsapp mr-2"></i> Contact via WhatsApp
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Become a Contractor CTA */}
        <Card className="mt-12 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-2">Are You a Contractor?</h2>
            <p className="mb-6 opacity-90">
              Join Flippy Homes and connect with thousands of homeowners looking for your services.
              Get 1 month free, then continue with a subscription for only R150/month.
            </p>
            <Button 
              variant="secondary" 
              onClick={() => navigate('/contractor/register')}
              className="bg-white text-emerald-700 hover:bg-gray-100"
              data-testid="become-contractor-btn"
            >
              <i className="fas fa-user-plus mr-2"></i> Register as Contractor
            </Button>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default HomeServices;
